import { MedplumClient, resolveId } from '@medplum/core';
import { Appointment, EpisodeOfCare } from '@medplum/fhirtypes';
import {
  appointmentTypeTokens,
  CONSULTATION_APPOINTMENT_CODES,
  PROCEDURE_APPOINTMENT_CODES,
} from '@bariendo/lib/constants';
import { listRangeBounds } from '@/components/Hub/listRange';

/**
 * Which date the cohort window applies to.
 * `program-start` is the patient's EpisodeOfCare `period.start` — the same value the Carrum report
 * shows as "Account created" — because FHIR has no searchable Patient-creation date.
 */
export type CohortDateField = 'procedure' | 'consult' | 'program-start';

export interface CohortFilters {
  dateField: CohortDateField;
  /** Inclusive lower bound, `YYYY-MM-DD`. */
  from: string;
  /** Inclusive upper bound, `YYYY-MM-DD`. */
  to: string;
}

export const COHORT_DATE_FIELD_OPTIONS: Array<{ value: CohortDateField; label: string }> = [
  { value: 'procedure', label: 'Procedure date' },
  { value: 'consult', label: 'Consult date' },
  { value: 'program-start', label: 'Account created' },
];

/** Page size for cohort resolution — the window is small, so one page is normally enough. */
const COHORT_PAGE_SIZE = '1000';

function appointmentTypeParam(codes: readonly string[]): string {
  return appointmentTypeTokens(codes).join(',');
}

/**
 * Builds the server-side cohort search for a date window. Returns the resource type to search and
 * its params; the caller runs the search and extracts patient references.
 *
 * The date filter stays in the search params on purpose: `MockClient` does not implement the
 * `Appointment` `date` composite search, and filtering client-side to satisfy the mock would ship a
 * bug to production.
 */
export function buildCohortSearch(filters: CohortFilters): {
  resourceType: 'Appointment' | 'EpisodeOfCare';
  params: URLSearchParams;
} {
  // Full instants, the end exclusive on the midnight after `to`: Medplum
  // resolves a date-only `le` to midnight, which dropped the whole last day
  // of the window (BAR-2708).
  const { start, end } = listRangeBounds(filters);
  const params = new URLSearchParams();
  params.append('date', `ge${start}`);
  params.append('date', `lt${end}`);
  params.set('_count', COHORT_PAGE_SIZE);

  if (filters.dateField === 'program-start') {
    params.set('_elements', 'patient');
    return { resourceType: 'EpisodeOfCare', params };
  }

  params.set(
    'appointment-type',
    appointmentTypeParam(
      filters.dateField === 'procedure' ? PROCEDURE_APPOINTMENT_CODES : CONSULTATION_APPOINTMENT_CODES
    )
  );
  params.append('status:not', 'cancelled');
  params.append('status:not', 'entered-in-error');
  params.set('_elements', 'participant');

  return { resourceType: 'Appointment', params };
}

const PATIENT_REFERENCE_PREFIX = 'Patient/';

/**
 * Extracts de-duplicated, first-seen-ordered bare patient ids from cohort search results.
 * Appointments carry the patient as a participant; EpisodeOfCare carries it as `patient`.
 */
export function extractPatientIds(resources: Array<Appointment | EpisodeOfCare>): string[] {
  const ids = new Set<string>();

  for (const resource of resources) {
    const references =
      resource.resourceType === 'Appointment'
        ? (resource.participant ?? []).map((participant) => participant.actor)
        : [resource.patient];

    for (const reference of references) {
      if (!reference?.reference?.startsWith(PATIENT_REFERENCE_PREFIX)) {
        continue;
      }
      const id = resolveId(reference);
      if (id) {
        ids.add(id);
      }
    }
  }

  return Array.from(ids);
}

/** Runs the cohort search for a date window and resolves it to bare patient ids. */
export async function fetchCohortPatientIds(medplum: MedplumClient, filters: CohortFilters): Promise<string[]> {
  const { resourceType, params } = buildCohortSearch(filters);
  const resources: Array<Appointment | EpisodeOfCare> = [];

  for await (const page of medplum.searchResourcePages(resourceType, params)) {
    resources.push(...(page as Array<Appointment | EpisodeOfCare>));
  }

  return extractPatientIds(resources);
}
